import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StoredOrganization } from '@/types/organizations';
import { StoredDocument } from '@/types/documents';
import {
  Building, AlertCircle, Users, Target,
  DollarSign, Calendar, ChevronLeft,
  Mail, Link as LinkIcon, MapPin, GiftIcon, ArrowUpRight,
  BookOpen, Briefcase, FileText, Tag, Award, Newspaper
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from "@/components/ui/use-toast";
import { formatDate } from '@/lib/date';

const OrganizationDetail: React.FC = () => {
  console.log("OrganizationDetail rendering");
  const params = useParams();
  console.log("Route params:", params);
  const organizationId = params.organizationId;

  const navigate = useNavigate();
  const [organization, setOrganization] = useState<StoredOrganization | null>(null);
  const [documents, setDocuments] = useState<StoredDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!organizationId) {
        console.error("Organization ID is missing:", organizationId);
        setError("Organization ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Log the request for debugging
        console.log(`Fetching organization with ID: ${organizationId}`);

        // Make the URL very clear
        const url = `/api/v1/organizations/${organizationId}`;
        console.log("Making request to:", url);

        const response = await fetch(url);

        // Log the complete response for debugging
        console.log(`Response:`, response);

        if (response.status === 404) {
          setError("Organization not found");
          setLoading(false);
          return;
        }

        if (!response.ok) {
          // Try to get error details from response
          let errorDetail;
          try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorData.message || 'Unknown error';
          } catch {
            errorDetail = response.statusText;
          }

          throw new Error(`Server Error: ${errorDetail}`);
        }

        const data = await response.json();
        console.log('Organization data:', data);

        // Validate the received data has required fields
        if (!data.names || !data.org_type || !data.category) {
          throw new Error('Received invalid organization data format');
        }

        setOrganization(data);

        // Also fetch organization documents
        fetchOrganizationDocuments(organizationId);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch organization');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [organizationId]);

  // And update this function to also use relative URLs
  const fetchOrganizationDocuments = async (orgId: string) => {
    try {
      // Use relative URL path
      const response = await fetch(`/api/v1/organizations/${orgId}/documents?limit=20`);

      if (!response.ok) {
        throw new Error(`Failed to fetch organization documents: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Organization documents:', data);

      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Error fetching organization documents:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load organization documents. Please try again later."
      });
    }
  };

  // Safe formatDate helper that handles null/undefined
  const safeFormatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="mb-8 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/organizations')}>
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Organizations
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/organizations')}>
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Organizations
          </Button>
        </div>

        <Alert variant="destructive" className="max-w-xl mx-auto">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Organization not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <header className="mb-8">
        <Button variant="ghost" onClick={() => navigate('/organizations')} className="mb-4">
          <ChevronLeft className="h-5 w-5 mr-1" />
          All Organizations
        </Button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              {organization.names?.primary_name || 'Unnamed Organization'}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="outline">{organization.org_type || 'Unknown Type'}</Badge>
              <Badge variant="secondary">{organization.category || 'Uncategorized'}</Badge>
              <Badge
                className={
                  organization.status === 'active' ? 'bg-green-100 text-green-800' :
                  organization.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }
              >
                {organization.status || 'Unknown Status'}
              </Badge>
              {organization.flagged && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Flagged
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left 2/3 */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="people">Key People</TabsTrigger>
              <TabsTrigger value="mission">Mission & Impact</TabsTrigger>
              <TabsTrigger value="programs">Programs</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="space-y-6">
                {/* Organization Synopsis if available */}
                {organization.org_synopsis && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <Newspaper className="h-5 w-5" />
                          Organization Synopsis
                        </CardTitle>
                        <div className="text-sm text-gray-500">
                          Version {organization.org_synopsis.version || '1'} • Last updated: {safeFormatDate(organization.org_synopsis.timestamp)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="prose max-w-none">
                      <p className="whitespace-pre-line">{organization.org_synopsis.synopsis || 'No synopsis available.'}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Identity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Organization Identity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Primary Name</h3>
                          <p className="mt-1 text-lg font-medium">{organization.names?.primary_name || 'Not specified'}</p>
                        </div>

                        {/* Tax ID */}
                        {organization.identity?.tax_id && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Tax ID</h3>
                            <p className="mt-1">{organization.identity.tax_id}</p>
                          </div>
                        )}

                        {/* Founded Date */}
                        {organization.identity?.founding_date && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Founded</h3>
                            <p className="mt-1 flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              {safeFormatDate(organization.identity.founding_date)}
                            </p>
                          </div>
                        )}

                        {/* Languages */}
                        {organization.identity?.languages && organization.identity.languages.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Languages</h3>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {organization.identity.languages.map((lang, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {lang}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Aliases */}
                      {organization.names?.aliases && organization.names.aliases.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-2">Also Known As</h3>
                          <div className="flex flex-wrap gap-2">
                            {organization.names.aliases.map((alias, index) => (
                              <Badge key={index} variant="secondary">
                                {alias}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Abbreviations */}
                      {organization.names?.abbreviations && organization.names.abbreviations.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-2">Abbreviations</h3>
                          <div className="flex flex-wrap gap-2">
                            {organization.names.abbreviations.map((abbr, index) => (
                              <Badge key={index} variant="outline">
                                {abbr}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Primary Contact */}
                      <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700">Primary Location</h3>

                        {/* Address */}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div>
                            <p>{organization.contact_info?.address_line1 || 'Address not specified'}</p>
                            {organization.contact_info?.address_line2 && (
                              <p>{organization.contact_info.address_line2}</p>
                            )}
                            <p>
                              {[
                                organization.contact_info?.city,
                                organization.contact_info?.state,
                                organization.contact_info?.postal_code
                              ].filter(Boolean).join(', ') || 'Location not specified'}
                            </p>
                            <p>{organization.contact_info?.country || ''}</p>
                          </div>
                        </div>

                        {/* Phone, Email & Website */}
                        {organization.contact_info?.phone && (
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            <a href={`tel:${organization.contact_info.phone}`} className="text-blue-600 hover:underline">
                              {organization.contact_info.phone}
                            </a>
                          </div>
                        )}

                        {organization.contact_info?.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <a href={`mailto:${organization.contact_info.email}`}
                              className="text-blue-600 hover:underline">
                              {organization.contact_info.email}
                            </a>
                          </div>
                        )}

                        {organization.contact_info?.website && (
                          <div className="flex items-center gap-2">
                            <LinkIcon className="h-4 w-4 text-gray-400" />
                            <a href={organization.contact_info.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline">
                              {organization.contact_info.website}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Social Media */}
                      {organization.contact_info?.social_media &&
                       organization.contact_info.social_media &&
                       Object.values(organization.contact_info.social_media).some(Boolean) && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-3">Social Media</h3>
                          <div className="flex flex-wrap gap-3">
                            {organization.contact_info.social_media.twitter && (
                              <a href={organization.contact_info.social_media.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-500 hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                                Twitter
                              </a>
                            )}
                            {organization.contact_info.social_media.linkedin && (
                              <a href={organization.contact_info.social_media.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-700 hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                                LinkedIn
                              </a>
                            )}
                            {organization.contact_info.social_media.facebook && (
                              <a href={organization.contact_info.social_media.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                                Facebook
                              </a>
                            )}
                            {organization.contact_info.social_media.instagram && (
                              <a href={organization.contact_info.social_media.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-pink-600 hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                                Instagram
                              </a>
                            )}
                            {organization.contact_info.social_media.tiktok && (
                              <a href={organization.contact_info.social_media.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-black hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path><path d="M15 8a4 4 0 0 0-4-4v12a4 4 0 0 0 4-4v-4z"></path><path d="M15 8h4v4"></path></svg>
                                TikTok
                              </a>
                            )}
                            {organization.contact_info.social_media.bluesky && (
                              <a href={organization.contact_info.social_media.bluesky} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:underline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12A10 10 0 1 1 12 2a10 10 0 0 1 10 10z"></path><path d="M15 9.354a4 4 0 1 0 0 5.292"></path></svg>
                                Bluesky
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Additional Locations */}
                {organization.additional_locations && organization.additional_locations.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Additional Locations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {organization.additional_locations.map((location, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                              <div>
                                <p>{location.address_line1 || 'Address not specified'}</p>
                                {location.address_line2 && <p>{location.address_line2}</p>}
                                <p>
                                  {[
                                    location.city,
                                    location.state,
                                    location.postal_code
                                  ].filter(Boolean).join(', ') || 'Location not specified'}
                                </p>
                                <p>{location.country || ''}</p>
                                {location.phone && <p className="mt-1">Phone: {location.phone}</p>}
                                {location.email && <p>Email: {location.email}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Key People Tab */}
            <TabsContent value="people">
              <div className="space-y-6">
                {organization.key_people && organization.key_people.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        People & Leadership
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {organization.key_people.map((person, index) => (
                          <div
                            key={index}
                            className="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">{person.name || 'Unknown'}</span>
                              </div>
                              {person.title && (
                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                  <Briefcase className="h-4 w-4" />
                                  {person.title}
                                </div>
                              )}
                              {person.email && (
                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                  <Mail className="h-4 w-4" />
                                  <a href={`mailto:${person.email}`} className="text-blue-600 hover:underline">
                                    {person.email}
                                  </a>
                                </div>
                              )}
                              {person.context && (
                                <p className="mt-1 text-sm text-gray-500">{person.context}</p>
                              )}
                            </div>
                            {person.confidence_score !== undefined && (
                              <Badge className={`
                                ${person.confidence_score >= 0.8 ? 'bg-green-100 text-green-800' :
                                  person.confidence_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'}
                              `}>
                                {Math.round((person.confidence_score || 0) * 100)}% confidence
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="flex items-center justify-center h-32">
                      <p className="text-gray-500">No key people information available for this organization.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Add Key Person Button */}
                <div className="flex justify-end">
                  <Button variant="outline" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Add Key Person
                  </Button>
                </div>

                {/* Organization Structure - Optional Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Organization Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Board of Directors */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Board of Directors</h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-500 text-sm">No board information available.</p>
                        </div>
                      </div>

                      {/* Leadership Team */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Leadership Team</h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="space-y-2">
                            {organization.key_people
                              .filter(person => person.title && person.title.toLowerCase().includes('director') ||
                                                person.title?.toLowerCase().includes('chief') ||
                                                person.title?.toLowerCase().includes('president') ||
                                                person.title?.toLowerCase().includes('head'))
                              .slice(0, 5)
                              .map((person, index) => (
                                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium">{person.name}</span>
                                  </div>
                                  <span className="text-sm text-gray-600">{person.title}</span>
                                </div>
                              ))}
                            {organization.key_people.filter(person =>
                              person.title && (
                                person.title.toLowerCase().includes('director') ||
                                person.title.toLowerCase().includes('chief') ||
                                person.title.toLowerCase().includes('president') ||
                                person.title.toLowerCase().includes('head')
                              )
                            ).length === 0 && (
                              <p className="text-gray-500 text-sm">No leadership team information available.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Staff */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Staff</h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="space-y-2">
                            {organization.key_people
                              .filter(person => person.title &&
                                !person.title.toLowerCase().includes('director') &&
                                !person.title.toLowerCase().includes('chief') &&
                                !person.title.toLowerCase().includes('president') &&
                                !person.title.toLowerCase().includes('head'))
                              .slice(0, 5)
                              .map((person, index) => (
                                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium">{person.name}</span>
                                  </div>
                                  <span className="text-sm text-gray-600">{person.title}</span>
                                </div>
                              ))}
                            {organization.key_people.filter(person =>
                              person.title &&
                              !person.title.toLowerCase().includes('director') &&
                              !person.title.toLowerCase().includes('chief') &&
                              !person.title.toLowerCase().includes('president') &&
                              !person.title.toLowerCase().includes('head')
                            ).length === 0 && (
                              <p className="text-gray-500 text-sm">No staff information available.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Network - Optional Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Contact Network
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-gray-500">No contact network information available for this organization.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Mission & Impact Tab */}
            <TabsContent value="mission">
              <div className="space-y-6">
                {/* Mission & Vision */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Mission & Vision
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {organization.mission_info?.mission_statement && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-blue-900 mb-2">Mission Statement</h3>
                          <p className="text-blue-800">
                            {organization.mission_info.mission_statement}
                          </p>
                        </div>
                      )}

                      {organization.mission_info?.vision_statement && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-purple-900 mb-2">Vision Statement</h3>
                          <p className="text-purple-800">
                            {organization.mission_info.vision_statement}
                          </p>
                        </div>
                      )}

                      {/* Theory of Change */}
                      {organization.mission_info?.theory_of_change && (
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-green-900 mb-2">Theory of Change</h3>
                          <p className="text-green-800">
                            {organization.mission_info.theory_of_change}
                          </p>
                        </div>
                      )}

                      {/* Values */}
                      {organization.mission_info?.values && organization.mission_info.values.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-2">Core Values</h3>
                          <div className="flex flex-wrap gap-2">
                            {organization.mission_info.values.map((value, index) => (
                              <Badge key={index} variant="outline">
                                {value}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Impact Areas */}
                {organization.mission_info?.impact_areas && organization.mission_info.impact_areas.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Impact Areas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {organization.mission_info.impact_areas.map((area, index) => (
                            <Badge key={index} className="bg-green-100 text-green-800">
                              {area}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Target Demographics */}
                {organization.mission_info?.target_demographics && organization.mission_info.target_demographics.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Target Demographics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {organization.mission_info.target_demographics.map((demo, index) => (
                          <Badge key={index} variant="secondary">
                            <Users className="h-3 w-3 mr-1" />
                            {demo}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Impact Metrics */}
                {organization.impact_metrics && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Award className="h-5 w-5" />
                        Impact Metrics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Reach */}
                        {organization.impact_metrics.reach && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Reach</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <span className="text-sm text-gray-500">Direct</span>
                                <p className="text-lg font-medium">
                                  {(organization.impact_metrics.reach.direct || 0).toLocaleString()}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <span className="text-sm text-gray-500">Indirect</span>
                                <p className="text-lg font-medium">
                                  {(organization.impact_metrics.reach.indirect || 0).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Key Metrics */}
                        {organization.impact_metrics.key_metrics && organization.impact_metrics.key_metrics.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Key Metrics</h3>
                            <div className="space-y-2">
                              {organization.impact_metrics.key_metrics.map((metric, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                  <span className="text-sm text-gray-500">{metric.metric_name || 'Metric'}</span>
                                  <p className="text-lg font-medium">
                                    {(metric.value || 0).toLocaleString()} {metric.unit || ''}
                                    <span className="text-sm text-gray-500 ml-2">({metric.period || 'N/A'})</span>
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Geographical Reach */}
                        {organization.impact_metrics.geographical_reach && organization.impact_metrics.geographical_reach.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Geographical Reach</h3>
                            <div className="flex flex-wrap gap-2">
                              {organization.impact_metrics.geographical_reach.map((region, index) => (
                                <Badge key={index} variant="outline">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {region}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Assessment Date */}
                        {organization.impact_metrics.assessment_date && (
                          <div className="text-sm text-gray-500 mt-4 pt-4 border-t">
                            Impact metrics assessed on: {safeFormatDate(organization.impact_metrics.assessment_date)}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Alignment Scores */}
                {organization.alignment && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <Target className="h-5 w-5" />
                          Mission Alignment
                        </CardTitle>
                        <Badge>
                          {Math.round((organization.alignment.overall_score || 0) * 100)}% Overall Alignment
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {organization.alignment.scores && Object.values(organization.alignment.scores || {}).length > 0 && (
                          <div className="space-y-3">
                            {Object.values(organization.alignment.scores || {}).map((score, index) => (
                              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{score.dimension || 'Dimension'}</span>
                                  <Badge className={`
                                    ${(score.score || 0) >= 0.8 ? 'bg-green-100 text-green-800' :
                                      (score.score || 0) >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'}
                                  `}>
                                    {Math.round((score.score || 0) * 100)}%
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600">{score.evidence || 'No evidence provided'}</p>
                                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                  <span>Evaluated by: {score.evaluated_by || 'Unknown'}</span>
                                  <span>Date: {safeFormatDate(score.evaluation_date)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {organization.alignment.narrative && (
                          <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                            <h3 className="text-sm font-medium text-blue-900 mb-2">Alignment Narrative</h3>
                            <p className="text-blue-800">{organization.alignment.narrative}</p>
                          </div>
                        )}

                        {organization.alignment.last_evaluated && (
                          <div className="text-sm text-gray-500 mt-4 pt-4 border-t">
                            <p>Last evaluated: {safeFormatDate(organization.alignment.last_evaluated)}</p>
                            {organization.alignment.evaluation_period && (
                              <p>
                                Evaluation period: {safeFormatDate(organization.alignment.evaluation_period.start)} to {safeFormatDate(organization.alignment.evaluation_period.end)}
                              </p>
                            )}
                            {organization.alignment.next_review_date && (
                              <p>Next review: {safeFormatDate(organization.alignment.next_review_date)}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Programs Tab */}
            <TabsContent value="programs">
              {organization.programs && organization.programs.length > 0 ? (
                <div className="space-y-6">
                  {organization.programs.map((program, index) => (
                    <Card key={program.id || index}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5" />
                            {program.name || `Program ${index + 1}`}
                          </CardTitle>
                          <Badge variant={program.status === 'active' ? 'default' : 'secondary'}>
                            {program.status || 'Unknown Status'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {program.description && (
                            <p className="text-gray-600">{program.description}</p>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Focus Areas */}
                            {program.focus_areas?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Focus Areas</h4>
                                <div className="flex flex-wrap gap-2">
                                  {program.focus_areas.map((area, index) => (
                                    <Badge key={index} variant="outline">
                                      <Target className="h-3 w-3 mr-1" />
                                      {area}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Target Population */}
                            {program.target_population?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Target Population</h4>
                                <div className="flex flex-wrap gap-2">
                                  {program.target_population.map((target, index) => (
                                    <Badge key={index} variant="secondary">
                                      <Users className="h-3 w-3 mr-1" />
                                      {target}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Budget */}
                          {program.budget !== undefined && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Program Budget</h4>
                              <p className="text-lg font-medium">
                                ${(program.budget || 0).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {/* Outcomes */}
                          {program.outcomes?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Outcomes</h4>
                              <ul className="list-disc pl-5 space-y-1">
                                {program.outcomes.map((outcome, index) => (
                                  <li key={index} className="text-gray-600">{outcome}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Dates and Duration */}
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-4 pt-4 border-t">
                            {program.start_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>Started: {safeFormatDate(program.start_date)}</span>
                              </div>
                            )}
                            {program.end_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>Ends: {safeFormatDate(program.end_date)}</span>
                              </div>
                            )}
                          </div>

                          {/* Related Grants */}
                          {program.related_grants?.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Related Grants</h4>
                              <div className="flex flex-wrap gap-2">
                                {program.related_grants.map((grant, index) => (
                                  <Badge key={index} variant="outline">
                                    <GiftIcon className="h-3 w-3 mr-1" />
                                    {grant}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center h-32">
                    <p className="text-gray-500">No programs available for this organization.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Financial Tab */}
            <TabsContent value="financial">
              <div className="space-y-6">
                {/* Budget Overview */}
                {organization.financial_info?.budgets?.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Budget Overview
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {organization.financial_info.budgets.map((budget, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-medium">
                                {budget.budget_year || 'N/A'} {budget.fiscal_period && `(${budget.fiscal_period})`}
                              </h3>
                              <Badge variant="secondary">{budget.currency || 'USD'}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-gray-500">Total Budget</p>
                                <p className="text-lg font-medium">
                                  {(budget.budget_total ?? 0).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">YTD Budget</p>
                                <p className="text-lg font-medium">
                                  {(budget.budget_ytd ?? 0).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">YTD Revenue</p>
                                <p className="text-lg font-medium text-green-600">
                                  {(budget.revenue_ytd ?? 0).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">YTD Expenses</p>
                                <p className="text-lg font-medium text-red-600">
                                  {(budget.expenses_ytd ?? 0).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            {budget.context && (
                              <p className="mt-3 text-sm text-gray-600">{budget.context}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Budget Overview
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex items-center justify-center h-24">
                      <p className="text-gray-500">No budget information available.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Grants */}
                {organization.financial_info?.grants?.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <GiftIcon className="h-5 w-5" />
                        Grants
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {organization.financial_info.grants.map((grant, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium">{grant.grant_name || 'Unnamed Grant'}</h3>
                              <Badge variant="secondary">
                                {(grant.amount ?? 0).toLocaleString()} {grant.currency || 'USD'}
                              </Badge>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              <p>Source: {grant.grant_source || 'Unknown source'}</p>
                              {grant.grant_period && (
                                <p className="flex items-center gap-1 mt-1">
                                  <Calendar className="h-4 w-4" />
                                  Period: {grant.grant_period}
                                </p>
                              )}
                              {grant.associated_program && (
                                <p className="flex items-center gap-1 mt-1">
                                  <BookOpen className="h-4 w-4" />
                                  Associated Program: {grant.associated_program.name || 'Unnamed program'}
                                </p>
                              )}
                              {grant.context && (
                                <p className="mt-2">{grant.context}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                {/* Investments */}
                {organization.financial_info?.investments?.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ArrowUpRight className="h-5 w-5" />
                        Investments
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {organization.financial_info.investments.map((investment, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium">{investment.investment_name || 'Unnamed Investment'}</h3>
                              <Badge variant="secondary">
                                {(investment.amount ?? 0).toLocaleString()} {investment.currency || 'USD'}
                              </Badge>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              <p>Source: {investment.investment_source || 'Unknown source'}</p>
                              <p className="flex items-center gap-1 mt-1">
                                <Target className="h-4 w-4" />
                                Stage: {investment.funding_stage || 'Unknown stage'}
                              </p>
                              {investment.date_received && (
                                <p className="flex items-center gap-1 mt-1">
                                  <Calendar className="h-4 w-4" />
                                  Received: {safeFormatDate(investment.date_received)}
                                </p>
                              )}
                              {investment.context && (
                                <p className="mt-2">{investment.context}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                {(!organization.financial_info?.grants || organization.financial_info.grants.length === 0) &&
                (!organization.financial_info?.investments || organization.financial_info.investments.length === 0) &&
                (!organization.financial_info?.budgets || organization.financial_info.budgets.length === 0) && (
                  <Card>
                    <CardContent className="flex items-center justify-center h-24">
                      <p className="text-gray-500">No financial information available for this organization.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Documents
                  </CardTitle>
                  <CardDescription>
                    Documents associated with this organization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {documents.length > 0 ? (
                    <div className="space-y-4">
                      {documents.map((doc, index) => (
                        <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                          <div>
                            <h3 className="font-medium">{doc.title || 'Untitled Document'}</h3>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {doc.asset_type || "Document"}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {safeFormatDate(doc.date)}
                              </span>
                            </div>
                            {doc.tags && doc.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {doc.tags.map((tag, tagIndex) => (
                                  <Badge key={tagIndex} variant="outline" className="text-xs">
                                    {tag.name || 'Unnamed tag'}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/documents/${doc.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <FileText className="h-8 w-8 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No documents associated with this organization yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Right 1/3 */}
        <div className="space-y-6">
          {/* Meta Information */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">Type</span>
                  <p>{organization.org_type || 'Not specified'}</p>
                </div>
                <Separator />
                <div>
                  <span className="text-sm text-gray-500">Category</span>
                  <p>{organization.category || 'Not specified'}</p>
                </div>
                <Separator />
                <div>
                  <span className="text-sm text-gray-500">Status</span>
                  <p className="capitalize">{organization.status || 'Not specified'}</p>
                </div>
                <Separator />
                {organization.identity?.founding_date && (
                  <>
                    <div>
                      <span className="text-sm text-gray-500">Founded</span>
                      <p>{safeFormatDate(organization.identity.founding_date)}</p>
                    </div>
                    <Separator />
                  </>
                )}
                {organization.identity?.tax_id && (
                  <>
                    <div>
                      <span className="text-sm text-gray-500">Tax ID</span>
                      <p>{organization.identity.tax_id}</p>
                    </div>
                    <Separator />
                  </>
                )}
                <div>
                  <span className="text-sm text-gray-500">Added to System</span>
                  <p>{safeFormatDate(organization.created_at)}</p>
                </div>
                <Separator />
                <div>
                  <span className="text-sm text-gray-500">Last Updated</span>
                  <p>{safeFormatDate(organization.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Documents</p>
                  <p className="text-xl font-medium">{documents.length}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Programs</p>
                  <p className="text-xl font-medium">{organization.programs?.length || 0}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Key People</p>
                  <p className="text-xl font-medium">{organization.key_people?.length || 0}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Impact Areas</p>
                  <p className="text-xl font-medium">{organization.mission_info?.impact_areas?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button className="w-full">
                  Edit Organization
                </Button>
                {organization.flagged ? (
                  <Button variant="outline" className="w-full flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Remove Flag
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    Flag Organization
                  </Button>
                )}
                <Button variant="outline" className="w-full">
                  Add Document
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity (placeholder) */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">New document added</p>
                    <p className="text-xs text-gray-500">1 day ago</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-full">
                    <Users className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Contact information updated</p>
                    <p className="text-xs text-gray-500">3 days ago</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-2 rounded-full">
                    <Building className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Organization profile created</p>
                    <p className="text-xs text-gray-500">7 days ago</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetail;